import type { CaseDraftResult, CasePolicyDraft, ValidationMessage } from '../../lib/api'

/**
 * Whether to offer "draft a policy from this case", and what to say about what came back.
 *
 * Both are decisions rather than layout, so both are here and tested, and the panel beside this file
 * is markup (D-186).
 *
 * The first decision is the one with a product argument behind it. Drafting is gated at
 * `policies:author`, which is owner and administrator - not `alerts:triage`, which includes the
 * analyst. An analyst triages and does not author, so an analyst does not see this control at all.
 * That is a deliberate asymmetry on a screen an analyst lives in, and it is worth being able to point
 * at the line where it is made.
 */

/** The roles the server's `policies:author` policy admits. Hiding, never enforcing: the API refuses either way. */
export const AUTHOR_ROLES = ['owner', 'administrator'] as const

export interface AssistantAvailability {
  /** A provider credential exists for this deployment. */
  available: boolean
  /** This organization has switched the assistant on. */
  enabled: boolean
}

export interface DraftOffer {
  shown: boolean
  /** Why it is not shown, for a comment in the markup rather than for the operator: nothing is rendered. */
  reason: string | null
}

/**
 * Hidden rather than disabled, which is the opposite of this console's usual habit.
 *
 * A disabled control is right when the reason is about this row and the operator could change it - a
 * resolve button with no note typed yet. It is wrong here: an analyst cannot become an author by
 * doing anything on this screen, and an organization with no assistant cannot switch one on from a
 * case. A permanently grayed button on every case in the queue would be a standing advertisement for
 * something the person reading it cannot have.
 */
export function draftOffer(roles: readonly string[], assistant: AssistantAvailability | null): DraftOffer {
  if (!AUTHOR_ROLES.some((role) => roles.includes(role)))
    return { shown: false, reason: 'Drafting a policy needs the authority to author one.' }

  if (assistant === null) return { shown: false, reason: 'The assistant settings have not been read yet.' }
  if (!assistant.available) return { shown: false, reason: 'This deployment has no model provider credential.' }
  if (!assistant.enabled) return { shown: false, reason: 'This organization has the assistant switched off.' }

  return { shown: true, reason: null }
}

/**
 * What came back.
 *
 * Five outcomes rather than two, because "it did not work" covers three situations that need three
 * different responses from the person reading it: a draft the validator refused can be fixed in the
 * editor, an assistant that is off is somebody else's setting, and a draft that saved but could not
 * be recorded against the case is a success with a footnote.
 */
export type DraftOutcome = 'drafted' | 'drafted-unrecorded' | 'refused' | 'unavailable' | 'failed'

export interface DraftReading {
  outcome: DraftOutcome
  message: string
  policyId: string | null
  findings: ValidationMessage[]
  /** The ai.request_audit entry this ask produced, so it can be found in the ledger. */
  requestId: string | null
}

export function readDraft(result: CaseDraftResult): DraftReading {
  const findings = result.findings ?? []

  if (!result.saved) {
    // An assistant that is switched off never reaches here - that answer is a 503 and arrives as a
    // thrown error, read by `readDraftFailure` below. This is the model having answered with
    // something that is not a policy, or with a policy that will not validate.
    return {
      outcome: 'refused',
      message:
        result.problem ??
        (findings.length > 0
          ? 'The draft needs a change before it can be saved. The findings say which.'
          : 'The assistant did not produce a policy for this case.'),
      policyId: null,
      findings,
      requestId: result.requestId ?? null
    }
  }

  const where = result.scope?.explanation ?? ''

  if (result.provenanceRecorded === false)
    return {
      outcome: 'drafted-unrecorded',
      // The draft is the thing that mattered and it exists. Saying so first, and the footnote second,
      // is the honest order: the alternative wording makes a successful draft read as a failure.
      message: `Saved as a draft. ${where} ${result.provenanceProblem ?? 'This case could not record that it produced one.'}`.trim(),
      policyId: result.policyId ?? null,
      findings,
      requestId: result.requestId ?? null
    }

  return {
    outcome: 'drafted',
    message: `Saved as a draft for review. ${where}`.trim(),
    policyId: result.policyId ?? null,
    findings,
    requestId: result.requestId ?? null
  }
}

/**
 * A thrown failure, told apart by status.
 *
 * 503 is the assistant being unavailable and is the one worth wording carefully: it is not this
 * person's fault and not something they can fix from a case, so the message says who can.
 */
export function readDraftFailure(status: number | null, message: string): DraftReading {
  if (status === 503)
    return {
      outcome: 'unavailable',
      message: 'The assistant is not available. An owner can switch it on and supply a provider credential under Settings.',
      policyId: null,
      findings: [],
      requestId: null
    }

  if (status === 403)
    return {
      outcome: 'failed',
      message: 'Drafting a policy needs the authority to author one. An administrator or the owner can do this.',
      policyId: null,
      findings: [],
      requestId: null
    }

  return { outcome: 'failed', message, policyId: null, findings: [], requestId: null }
}

/**
 * The drafts a case has already produced, trimmed to what is worth reading.
 *
 * Written after watching the panel with seventeen of them on it, which is a thing that happens to a
 * case somebody is testing against and would equally happen to a case two analysts both worked. The
 * list was unbounded and every line read "Revision 1" - each draft is a new policy, so the revision
 * number is 1 for all of them and carries nothing. A list where every row says the same word is not a
 * list.
 *
 * So: the newest few, and a count of the rest. The count matters more than the rows it replaces,
 * because the question this panel answers is "has somebody already done this", and the answer is the
 * number.
 */
export const DRAFTS_SHOWN = 3

export interface DraftHistory {
  shown: CasePolicyDraft[]
  hidden: number
  /** Null when nothing is hidden: a count of zero is a sentence nobody needs to read. */
  more: string | null
}

export function draftHistory(drafts: readonly CasePolicyDraft[], limit = DRAFTS_SHOWN): DraftHistory {
  const shown = drafts.slice(0, limit)
  const hidden = Math.max(0, drafts.length - shown.length)

  return {
    shown,
    hidden,
    more: hidden === 0 ? null : `and ${hidden} earlier draft${hidden === 1 ? '' : 's'} from this case`
  }
}

/**
 * How one of them reads.
 *
 * The revision is named only when it is not the first, because a draft from a case creates a new
 * policy and so is revision 1 almost always - printing it every time trains the eye to skip the line.
 * The device count is null-safe for the reason it is null-safe everywhere: a fleet size that could
 * not be read is not a fleet of none (D-263).
 */
export function describeDraft(draft: CasePolicyDraft, when: string): string {
  const revision = draft.revision > 1 ? `Revision ${draft.revision}, drafted ${when}` : `Drafted ${when}`

  const scope =
    draft.proposedDeviceCount === null
      ? 'proposed for the whole fleet'
      : `proposed for ${draft.proposedDeviceCount} device${draft.proposedDeviceCount === 1 ? '' : 's'}`

  return `${revision}, ${scope}`
}
